import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const Statement1 = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const currentUserId = localStorage.getItem("UserId");

  // ✅ Wrap fetchTransactions in useCallback
  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/statement`, {
        UserId: currentUserId
      });
      
      if (response.data.error) {
        setTransactions([]);
        if (response.data.error !== "No transactions found") {
          toast.info(response.data.error);
        }
      } else {
        // ✅ Ensure transactions is always an array
        setTransactions(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
      toast.error("Failed to load transaction history");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  // ✅ Add fetchTransactions to dependency array
  useEffect(() => {
    if (currentUserId) {
      fetchTransactions();
    }
  }, [currentUserId, fetchTransactions]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getTransactionIcon = (type, mode) => {
    const normalizedType = type?.toString().toUpperCase();
    if (normalizedType === 'CREDIT') return 'fa-arrow-up';
    if (normalizedType === 'DEBIT') return 'fa-arrow-down';
    return 'fa-exchange-alt';
  };

  const getTransactionColor = (type) => {
    const normalizedType = type?.toString().toUpperCase();
    return normalizedType === 'CREDIT' ? '#48bb78' : '#f56565';
  };

  const filteredTransactions = transactions.filter(transaction => {
    // Filter by type
    if (filter !== 'all' && transaction?.Type !== filter) {
      return false;
    }
    
    // Filter by date range
    if (dateRange.start) {
      try {
        const transDate = new Date(transaction?.Time);
        const startDate = new Date(dateRange.start);
        if (transDate < startDate) return false;
      } catch (e) {
        return false;
      }
    }
    
    if (dateRange.end) {
      try {
        const transDate = new Date(transaction?.Time);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59);
        if (transDate > endDate) return false;
      } catch (e) {
        return false;
      }
    }
    
    return true;
  });

  // ✅ FIX: Added count to the statistics
  const getStatistics = () => {
    // Safe check for empty array
    if (!Array.isArray(filteredTransactions) || filteredTransactions.length === 0) {
      return {
        totalCredit: 0,
        totalDebit: 0,
        balance: 0,
        count: 0 // ✅ Added count
      };
    }
    
    const totalCredit = filteredTransactions
      .filter(t => t?.Type?.toUpperCase() === 'CREDIT')
      .reduce((sum, t) => sum + (t?.amount || 0), 0);
    
    const totalDebit = filteredTransactions
      .filter(t => t?.Type?.toUpperCase() === 'DEBIT')
      .reduce((sum, t) => sum + (t?.amount || 0), 0);
    
    return {
      totalCredit,
      totalDebit,
      balance: totalCredit - totalDebit,
      count: filteredTransactions.length // ✅ Added count
    };
  };

  const stats = getStatistics();

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      
      <div className="statement-page">
        <div className="page-header">
          <h1>
            <i className="fa fa-file-invoice-dollar"></i>
            Main Wallet Statement
          </h1>
          <p>View all your main wallet transactions</p>
        </div>

        {/* Statistics Cards */}
        <div className="stats-cards">
          <div className="stat-card credit">
            <div className="stat-icon">
              <i className="fa fa-arrow-up"></i>
            </div>
            <div className="stat-info">
              <span className="stat-label">Total Credit</span>
              <span className="stat-value">{formatAmount(stats.totalCredit)}</span>
            </div>
          </div>
          
          <div className="stat-card debit">
            <div className="stat-icon">
              <i className="fa fa-arrow-down"></i>
            </div>
            <div className="stat-info">
              <span className="stat-label">Total Debit</span>
              <span className="stat-value">{formatAmount(stats.totalDebit)}</span>
            </div>
          </div>
          
          <div className="stat-card balance">
            <div className="stat-icon">
              <i className="fa fa-wallet"></i>
            </div>
            <div className="stat-info">
              <span className="stat-label">Balance</span>
              <span className="stat-value">{formatAmount(stats.balance)}</span>
            </div>
          </div>
          
          <div className="stat-card count">
            <div className="stat-icon">
              <i className="fa fa-list"></i>
            </div>
            <div className="stat-info">
              <span className="stat-label">Transactions</span>
              <span className="stat-value">{stats.count}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filter-group">
            <label>Transaction Type</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All Transactions</option>
              <option value="Credit">Credit Only</option>
              <option value="Debit">Debit Only</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Start Date</label>
            <input 
              type="date" 
              value={dateRange.start}
              onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
            />
          </div>
          
          <div className="filter-group">
            <label>End Date</label>
            <input 
              type="date" 
              value={dateRange.end}
              onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
            />
          </div>
          
          <button 
            className="btn-clear"
            onClick={() => {
              setFilter('all');
              setDateRange({ start: '', end: '' });
            }}
          >
            <i className="fa fa-times"></i>
            Clear Filters
          </button>
          
          <button className="btn-refresh" onClick={fetchTransactions}>
            <i className="fa fa-refresh"></i>
            Refresh
          </button>
        </div>

        {/* Transactions Table */}
        <div className="transactions-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading transactions...</p>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="empty-state">
              <i className="fa fa-inbox"></i>
              <p>No transactions found</p>
              <p className="empty-subtitle">Your transaction history will appear here</p>
            </div>
          ) : (
            <div className="transactions-list">
              {filteredTransactions.map((transaction, index) => (
                <div key={transaction?.Tid || index} className="transaction-item">
                  <div className="transaction-icon" style={{ backgroundColor: `${getTransactionColor(transaction?.Type)}20` }}>
                    <i className={`fa ${getTransactionIcon(transaction?.Type, transaction?.Mode)}`} 
                       style={{ color: getTransactionColor(transaction?.Type) }}></i>
                  </div>
                  <div className="transaction-details">
                    <div className="transaction-header">
                      <span className="transaction-desc">{transaction?.Desc || 'N/A'}</span>
                      <span className="transaction-amount" style={{ color: getTransactionColor(transaction?.Type) }}>
                        {transaction?.Type === 'Credit' ? '+' : '-'} {formatAmount(transaction?.amount)}
                      </span>
                    </div>
                    <div className="transaction-footer">
                      <span className="transaction-meta">
                        <i className="fa fa-tag"></i>
                        {transaction?.Mode || 'N/A'}
                      </span>
                      <span className="transaction-meta">
                        <i className="fa fa-clock"></i>
                        {formatDate(transaction?.Time)}
                      </span>
                      <span className="transaction-meta">
                        <i className="fa fa-hashtag"></i>
                        ID: {transaction?.Tid || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .statement-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          animation: fadeIn 0.5s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .page-header {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .page-header h1 {
          font-size: 32px;
          font-weight: 800;
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 10px;
        }
        
        .page-header h1 i {
          margin-right: 10px;
        }
        
        .page-header p {
          color: #718096;
          font-size: 16px;
        }
        
        /* Statistics Cards */
        .stats-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }
        
        .stat-card {
          background: white;
          border-radius: 15px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 15px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          transition: transform 0.2s;
        }
        
        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 20px rgba(0,0,0,0.1);
        }
        
        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }
        
        .stat-card.credit .stat-icon {
          background: #48bb7820;
          color: #48bb78;
        }
        
        .stat-card.debit .stat-icon {
          background: #f5656520;
          color: #f56565;
        }
        
        .stat-card.balance .stat-icon {
          background: #667eea20;
          color: #667eea;
        }
        
        .stat-card.count .stat-icon {
          background: #ed893620;
          color: #ed8936;
        }
        
        .stat-info {
          flex: 1;
        }
        
        .stat-label {
          display: block;
          font-size: 13px;
          color: #718096;
          margin-bottom: 5px;
        }
        
        .stat-value {
          display: block;
          font-size: 24px;
          font-weight: 800;
          color: #2d3748;
        }
        
        /* Filters */
        .filters-section {
          background: white;
          border-radius: 15px;
          padding: 20px;
          margin-bottom: 30px;
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
          align-items: flex-end;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }
        
        .filter-group {
          flex: 1;
          min-width: 150px;
        }
        
        .filter-group label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #4a5568;
          margin-bottom: 5px;
          text-transform: uppercase;
        }
        
        .filter-group select,
        .filter-group input {
          width: 100%;
          padding: 10px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .filter-group select:focus,
        .filter-group input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        
        .btn-clear, .btn-refresh {
          padding: 10px 20px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .btn-clear {
          background: #f7fafc;
          color: #4a5568;
          border: 1px solid #e2e8f0;
        }
        
        .btn-clear:hover {
          background: #edf2f7;
        }
        
        .btn-refresh {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
        }
        
        .btn-refresh:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
        }
        
        /* Transactions */
        .transactions-container {
          background: white;
          border-radius: 15px;
          padding: 20px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }
        
        .transactions-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        
        .transaction-item {
          display: flex;
          gap: 15px;
          padding: 15px;
          border-radius: 12px;
          background: #f7fafc;
          transition: all 0.2s;
        }
        
        .transaction-item:hover {
          background: #edf2f7;
          transform: translateX(5px);
        }
        
        .transaction-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          flex-shrink: 0;
        }
        
        .transaction-details {
          flex: 1;
        }
        
        .transaction-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 10px;
        }
        
        .transaction-desc {
          font-weight: 600;
          color: #2d3748;
          font-size: 16px;
        }
        
        .transaction-amount {
          font-weight: 800;
          font-size: 18px;
        }
        
        .transaction-footer {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }
        
        .transaction-meta {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          color: #718096;
        }
        
        .transaction-meta i {
          font-size: 12px;
        }
        
        /* Loading & Empty States */
        .loading-state, .empty-state {
          text-align: center;
          padding: 60px 20px;
        }
        
        .spinner {
          width: 50px;
          height: 50px;
          border: 3px solid #edf2f7;
          border-top-color: #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .empty-state i {
          font-size: 64px;
          color: #cbd5e0;
          margin-bottom: 20px;
        }
        
        .empty-state p {
          color: #718096;
          font-size: 18px;
          margin-bottom: 10px;
        }
        
        .empty-subtitle {
          font-size: 14px;
          color: #a0aec0;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .stats-cards {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .filters-section {
            flex-direction: column;
          }
          
          .filter-group {
            width: 100%;
          }
          
          .btn-clear, .btn-refresh {
            width: 100%;
            justify-content: center;
          }
          
          .transaction-header {
            flex-direction: column;
          }
          
          .transaction-footer {
            flex-direction: column;
            gap: 5px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default Statement1;